import { resolveLogoUrl } from "@/lib/utils/resolveLogoUrl";
import { buildFeatureSettingsFormPatch } from "./companyFeatureSettings";

export const getCompanyId = (data) =>
  data?.id ?? data?._id ?? data?.companyId ?? null;

// The API treats the company targetFrequency as a non-retroactive default:
// it seeds NEW metric assignments/targets and never rewrites existing ones
// (per-KPI choices made on Target Setup persist). Updates therefore send it
// only when the admin actually changed it, keeping unrelated saves minimal.
// Create flow (nothing to diff against) always sends.
export const shouldSendTargetFrequency = ({ form = {}, company } = {}) => {
  if (!company || typeof company !== "object") return true;
  const nextFrequency =
    form?.targetFrequency === "monthly" ? "monthly" : "yearly";
  const previousFrequency =
    company?.targetFrequency === "monthly" ? "monthly" : "yearly";
  return nextFrequency !== previousFrequency;
};

// Single source of truth for the GHG-baseline slice of the form, shared by
// company.store.js's createForm(), CompanySetupWizard's COMPANY_FORM_DEFAULTS
// and normalizeCompanyFormValues — previously copy-pasted across all three.
export const GHG_BASELINE_FORM_DEFAULTS = {
  // "" means "not configured yet" — distinct from either real mode, so a
  // company the admin never touches this step for isn't silently saved as
  // "reportingYear" (see normalizeGhgBaselineFormValues and
  // buildCompanyRequestPayload, which only send ghgBaselineMode once it's
  // one of the two real modes).
  ghgBaselineMode: "",
  ghgBaselineReportingYear: "",
  ghgBaselineReportingBoundaryId: "",
  ghgBaselineStartMonth: "",
  ghgBaselineEndYear: "",
  ghgBaselineEndMonth: "",
  ghgBaselineScope1: "",
  ghgBaselineScope2: "",
  ghgBaselineScope3: "",
  ghgBaselineEvidence: null,
};

export const normalizeGhgBaselineFormValues = (form = {}) => ({
  // Only coerce a recognized mode; anything else (including unset) stays
  // "" rather than silently becoming "reportingYear" — see
  // GHG_BASELINE_FORM_DEFAULTS.
  ghgBaselineMode:
    form?.ghgBaselineMode === "custom"
      ? "custom"
      : form?.ghgBaselineMode === "reportingYear"
        ? "reportingYear"
        : "",
  ghgBaselineReportingYear: form?.ghgBaselineReportingYear || "",
  ghgBaselineReportingBoundaryId: form?.ghgBaselineReportingBoundaryId || "",
  ghgBaselineStartMonth: form?.ghgBaselineStartMonth || "",
  ghgBaselineEndYear: form?.ghgBaselineEndYear || "",
  ghgBaselineEndMonth: form?.ghgBaselineEndMonth || "",
  ghgBaselineScope1: form?.ghgBaselineScope1 || "",
  ghgBaselineScope2: form?.ghgBaselineScope2 || "",
  ghgBaselineScope3: form?.ghgBaselineScope3 || "",
  ghgBaselineEvidence: form?.ghgBaselineEvidence || null,
});

// Shared by CompanySetupWizard (Formik hydration) and CompanyStore
// (fetchCompany draft hydration) — keep these in sync in one place rather
// than maintaining parallel copies.
export const buildCompanyFormPatch = (company) => {
  if (!company || typeof company !== "object") return null;
  const patch = {};
  const name = company.companyName ?? company.name;
  if (name) patch.companyName = name;
  if (company.country) patch.country = company.country;
  if (company.industry) patch.industry = company.industry;
  if (company.employeesRange) patch.employeesRange = company.employeesRange;
  if (company.revenueRange) patch.revenueRange = company.revenueRange;
  if (Array.isArray(company.serviceCountries)) {
    patch.serviceCountries = company.serviceCountries;
  }
  if (Array.isArray(company.objectives)) patch.objectives = company.objectives;
  if (company.targetFrequency) patch.targetFrequency = company.targetFrequency;
  if (company.dashboardVariant) {
    patch.dashboardVariant =
      company.dashboardVariant === "benchmark" ? "benchmark" : "standard";
  }
  if (typeof company.enableMonthlyYearlySwitch === "boolean") {
    patch.enableMonthlyYearlySwitch = company.enableMonthlyYearlySwitch;
  }
  if (typeof company.enableViewDetailsSwitch === "boolean") {
    patch.enableViewDetailsSwitch = company.enableViewDetailsSwitch;
  }
  if (typeof company.useJJOrganizationSelector === "boolean") {
    patch.useJJOrganizationSelector = company.useJJOrganizationSelector;
  }
  if (typeof company.showOnlyMetricsWithTargetAndData === "boolean") {
    patch.showOnlyMetricsWithTargetAndData =
      company.showOnlyMetricsWithTargetAndData;
  }

  if (typeof company.allowAssetList === "boolean") {
    patch.allowAssetList = company.allowAssetList;
  }
  // Unconditional, unlike the fields above: the server is the sole source of
  // truth for the GHG baseline slice, so every key is always set here — even
  // to its empty default — so a genuinely-empty server value can overwrite a
  // stale local/draft value instead of silently leaving it in place.
  Object.assign(patch, mapGhgBaselineFromCompany(company));
  // Unconditional too: the company's feature settings replace a previous
  // company's, and no edit carries over.
  Object.assign(patch, buildFeatureSettingsFormPatch(company));

  return Object.keys(patch).length > 0 ? patch : null;
};

export const mapGhgBaselineFromCompany = (company = {}) => ({
  // A company the server considers unconfigured (mode null/unset) must
  // hydrate back to "" here, not "reportingYear" — otherwise the wizard
  // and summary both read it as already configured, and saving anything
  // else on the form re-sends "reportingYear" as if the admin had chosen
  // it.
  ghgBaselineMode:
    company?.ghgBaselineMode === "custom"
      ? "custom"
      : company?.ghgBaselineMode === "reportingYear"
        ? "reportingYear"
        : "",
  ghgBaselineReportingBoundaryId: company?.ghgBaselineReportingBoundaryId
    ? String(company.ghgBaselineReportingBoundaryId)
    : "",
  ghgBaselineReportingYear:
    typeof company?.ghgBaselineStartYear === "number"
      ? String(company.ghgBaselineStartYear)
      : "",
  ghgBaselineStartMonth: company?.ghgBaselineStartMonth ?? "",
  ghgBaselineEndYear: company?.ghgBaselineEndYear ?? "",
  ghgBaselineEndMonth: company?.ghgBaselineEndMonth ?? "",
  ghgBaselineScope1:
    typeof company?.ghgBaselineScope1 === "number"
      ? String(company.ghgBaselineScope1)
      : "",
  ghgBaselineScope2:
    typeof company?.ghgBaselineScope2 === "number"
      ? String(company.ghgBaselineScope2)
      : "",
  ghgBaselineScope3:
    typeof company?.ghgBaselineScope3 === "number"
      ? String(company.ghgBaselineScope3)
      : "",
  ghgBaselineEvidence: company?.ghgBaselineEvidenceKey
    ? {
        url: resolveLogoUrl(company.ghgBaselineEvidenceUrl),
        name: company.ghgBaselineEvidenceFileName || undefined,
      }
    : null,
});
